import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import { CATALOG } from '../data/catalog';
import { useFlowstateAuth } from '../context/AuthContext';
import { SHOW_PAYMENT_FLOW } from '../data/pricing';
import { useThemeContext } from '../context/ThemeContext';

const indianNames = [
    'Aarav Sharma', 'Priya Patel', 'Rahul Verma', 'Sneha Iyer',
    'Vikram Singh', 'Ananya Gupta', 'Rohan Desai', 'Kavya Reddy',
    'Aditya Joshi', 'Neha Kapoor', 'Karan Malhotra', 'Riya Jain',
    'Siddharth Rao', 'Pooja Nair', 'Arjun Kapoor', 'Meera Rajput'
];

const indianLocations = [
    'Mumbai, MH', 'Bengaluru, KA', 'Delhi, DL', 'Hyderabad, TS',
    'Chennai, TN', 'Pune, MH', 'Kolkata, WB', 'Ahmedabad, GJ',
    'Noida, UP', 'Gurugram, HR', 'Indore, MP', 'Chandigarh, CH',
    'Jaipur, RJ', 'Lucknow, UP'
];

const times = [
    '2 mins ago', '5 mins ago', '10 mins ago', 'Just now',
    '1 min ago', '15 mins ago', '3 mins ago'
];

const cardColors = {
    javascript: '#f59e0b',
    react: '#3b82f6',
    systemDesign: '#10b981',
    dsa: '#8b5cf6',
    frontendSystemDesign: '#ec4899',
    interviewPrep: '#14b8a6',
    aiFundamentals: '#eab308',
    aiEngineering: '#ef4444',
    foundation: '#f59e0b',
    completeInterviewKit: '#3b82f6'
};

export const SocialProofToast = () => {
    const { user } = useFlowstateAuth();
    const { mode } = useThemeContext();
    const isLight = mode === 'light';

    const [socialProof, setSocialProof] = useState(null);

    useEffect(() => {
        if (!SHOW_PAYMENT_FLOW) return;

        let showTimeout;
        let hideTimeout;

        const scheduleNext = () => {
            showTimeout = setTimeout(() => {
                const allKeys = Object.keys(CATALOG);
                const unownedKeys = allKeys.filter(key => !(user?.purchasedModules?.includes(key)));
                const pool = unownedKeys.length > 0 ? unownedKeys : allKeys;
                const randomKey = pool[Math.floor(Math.random() * pool.length)];

                setSocialProof({
                    name: indianNames[Math.floor(Math.random() * indianNames.length)],
                    location: indianLocations[Math.floor(Math.random() * indianLocations.length)],
                    time: times[Math.floor(Math.random() * times.length)],
                    viewers: Math.floor(Math.random() * 35) + 12,
                    categoryKey: randomKey
                });

                hideTimeout = setTimeout(() => {
                    setSocialProof(null);
                    scheduleNext();
                }, 6500);

            }, Math.random() * 14000 + 8000);
        };

        scheduleNext();

        return () => {
            clearTimeout(showTimeout);
            clearTimeout(hideTimeout);
        };
    }, [user]);

    return (
        <AnimatePresence>
            {socialProof && (() => {
                const proofData = CATALOG[socialProof.categoryKey];
                if (!proofData) return null;
                const proofPrimaryColor = cardColors[socialProof.categoryKey] || '#3b82f6';

                return (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        style={{
                            position: 'fixed', bottom: 32, left: 32, width: 340, zIndex: 9999
                        }}
                    >
                        <Box sx={{
                            background: isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(24, 24, 27, 0.95)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: isLight ? '0 10px 40px -10px rgba(0,0,0,0.1)' : '0 10px 40px -10px rgba(0,0,0,0.8)',
                            borderRadius: '16px', py: 2, px: 2.5,
                            border: isLight ? `1px solid rgba(0,0,0,0.05)` : `1px solid rgba(255,255,255,0.05)`,
                        }}>
                            <IconButton
                                onClick={() => setSocialProof(null)}
                                sx={{ position: 'absolute', top: 6, right: 6, padding: '4px' }}
                            >
                                <CloseIcon sx={{ fontSize: 14, color: isLight ? '#94a3b8' : '#71717a' }} />
                            </IconButton>

                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Avatar sx={{
                                    bgcolor: proofPrimaryColor, width: 42, height: 42, color: '#fff', fontSize: '1.2rem', fontWeight: 700,
                                    boxShadow: `0 4px 12px ${proofPrimaryColor}50`
                                }}>
                                    {socialProof.name.charAt(0)}
                                </Avatar>
                                <Box sx={{ flex: 1, pr: 1 }}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: isLight ? '#0f172a' : '#f8fafc', lineHeight: 1.2 }}>
                                        {socialProof.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: isLight ? '#64748b' : '#a1a1aa', mt: 0.3 }}>
                                        from {socialProof.location}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: proofPrimaryColor, fontWeight: 700, mt: 0.5 }}>
                                        enrolled in {proofData.title}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography sx={{ position: 'absolute', bottom: 12, right: 16, fontSize: '0.65rem', color: isLight ? '#cbd5e1' : '#52525b', fontWeight: 600 }}>
                                {socialProof.time}
                            </Typography>
                        </Box>
                    </motion.div>
                );
            })()}
        </AnimatePresence>
    );
};

export default SocialProofToast;
